import * as _ from 'lodash';
import { ControlLabel, FormGroup } from 'react-bootstrap';
import type { VoidFunctionComponent } from 'react';

import { CANNOT_DISABLE_NON_CUSTOM_TEAM_ROLES_ERROR } from '../../../../../shared/organizationPackages/errors';
import { UserOrgRoleManagement } from '../../../../../shared/organizations/types';
import { orgRolesList } from '../../../../helpers/roles';
import useRoles from '../../../../hooks/api/roles/useRoles';
import { ErrorAlert } from '../../../generic/ErrorAlert';
import Flex from '../../../generic/Flex';
import AccessGate from '../../../generic/Gates/AccessGate';
import { icons } from '../../../generic/Icon';
import Select from '../../../generic/Select/Select';
import Text from '../../../generic/Text';
import Toggle from '../../../generic/Toggle';
import TooltipIcon from '../../../generic/TooltipIcon';
import useUserRoleManagement from '../hooks/useUserRoleManagement';
import type { GeneralSettings } from '../../../../../shared/settings/general';

type Props = {
  settings: GeneralSettings;
  onUpdateSettings: (settings: Partial<GeneralSettings>) => void;
  isDisabled: boolean;
  errors: string[] | null;
};

/**
 * Allow users to set the default role for new users.
 * This is only available to premium organizations.
 */
const UsersSection: VoidFunctionComponent<Props> = ({ settings, isDisabled, onUpdateSettings, errors }) => {
  const { data: roles = [] } = useRoles();
  const { data: userRoleManagement } = useUserRoleManagement();
  const keyedRoles = _.keyBy(roles, (role) => role.id);

  const isUserRoleIDPManaged = userRoleManagement?.organization === UserOrgRoleManagement.IDP;
  const isDefaultRoleDisabled = isDisabled || isUserRoleIDPManaged;
  const defaultRole = settings.defaultRoleId ? keyedRoles[settings.defaultRoleId] : null;
  const selectedDefaultRole = defaultRole && !isUserRoleIDPManaged
    ? { label: defaultRole.name, value: defaultRole.id }
    : { label: 'None', value: null };

  return (
    <AccessGate level="premium" behavior="hide">
      {errors?.some((error) => error.includes(CANNOT_DISABLE_NON_CUSTOM_TEAM_ROLES_ERROR)) && (
        <ErrorAlert status={{ errors }} />
      )}
      <hr />
      <Text.H3>Users</Text.H3>
      <div>
        <FormGroup className="default-role">
          <Flex direction="column" align="stretch" gap="12px">
            <ControlLabel>Default Role for New Users</ControlLabel>
            <Select
              id="org-default-role"
              isCreatable={false}
              isDisabled={isDefaultRoleDisabled}
              value={selectedDefaultRole}
              options={orgRolesList(keyedRoles).map((role) => ({ label: role.name, value: role.id }))}
              onChange={(role) => {
                if (!role) {
                  return;
                }
                onUpdateSettings({
                  defaultRoleId: role.value,
                });
              }}
            />
            {isUserRoleIDPManaged && (
              <Text.Paragraph>
                This organization is using single sign-on for user role management.
                The default role for new users is managed via the identity provider.
              </Text.Paragraph>
            )}
          </Flex>
        </FormGroup>
        <FormGroup>
          <Flex justify="flex-start" gap="8px">
            <Toggle
              checked={!settings.disableNonCustomTeamUserRoles}
              isDisabled={isDisabled}
              onChange={(isChecked) => {
                onUpdateSettings({
                  disableNonCustomTeamUserRoles: !isChecked,
                });
              }}
            />
            <ControlLabel>
              Enable FOSSA default team roles{' '}
              <TooltipIcon
                id="disable-non-custom-team-user-roles-tooltip"
                tooltip="Disabling this will hide the built in roles that FOSSA offers out of the box."
                icon={icons.infoCircle}
              />
            </ControlLabel>
          </Flex>
        </FormGroup>
      </div>
    </AccessGate>
  );
};

export default UsersSection;
